import { useState } from "react";
import { ClipboardList, HistoryIcon, LayoutDashboard, Leaf } from "lucide-react";
import { useLocation } from "react-router-dom";

import { ArkLogoMark } from "@/components/_atoms/ark-logo-mark";
import { SidebarNavElement } from "@/components/_templates/sidebar/sidebar-nav-element";
import { SidebarSectionToggle } from "@/components/_templates/sidebar/sidebar-section-toggle";
import { FeatureFlagToggle } from "@/domain/_shared/feature-flag/components/feature-flag-toggle";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useCheckPermission } from "@/domain/_shared/permission/hooks/api/use-check-permission";
import { useClimateActionNavigationStore } from "@/domain/climate-action/components/climate-action-quick-navigation/use-climate-action-navigation-store";
import { IndicatorSidebarNavLink } from "@/domain/indicator/components/indicator-sidebar-nav-link";
import { InventorySidebarNavLink } from "@/domain/inventory/components/inventory-sidebar-nav-link";
import { useProjectNavigationStore } from "@/domain/project/components/project-quick-navigation/use-project-navigation-store";
import { ProjectWorkspaceSidebarNavLink } from "@/domain/project/components/project-workspace-sidebar-nav-link";
import { ScenarioSidebarNavLink } from "@/domain/scenario/scenario-model/components/scenario-model-sidebar-nav-link";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { makeWorkspaceUrl } from "@/domain/workspace/lib/make-workspace-url";

import { isOnClimateActionDetailPage } from "./is-on-climate-action-detail-page";
import { isOnProjectDetailPage } from "./is-on-project-detail-page";

export const SidebarAppWorkspaceElement = () => {
  const [expanded, setExpanded] = useState(true);

  const { currentWorkspace } = useCurrentWorkspaceContext();
  const location = useLocation();
  const { navigationContext } = useClimateActionNavigationStore();

  const isClimateActionTeamContext =
    navigationContext?.type === "Team" && isOnClimateActionDetailPage(location.pathname);

  const { navigationContext: projectNavigationContext } = useProjectNavigationStore();
  const isProjectTeamContext = projectNavigationContext?.type === "Team" && isOnProjectDetailPage(location.pathname);
  const { t } = useDomainTranslation("common");
  const { hasPermission: hasPermissionToManageWorkspace } = useCheckPermission({
    resource: "workspace",
    resourceId: currentWorkspace.id,
    action: "manage",
  });
  return (
    <>
      <SidebarSectionToggle
        label={currentWorkspace.name}
        expanded={expanded}
        onToggle={() => {
          setExpanded(!expanded);
        }}
        testId="sidebar-app-workspace-element"
      />
      {expanded && (
        <>
          <InventorySidebarNavLink workspace={currentWorkspace} />
          <ScenarioSidebarNavLink workspace={currentWorkspace} />
          <SidebarNavElement
            to={makeWorkspaceUrl({ workspace: currentWorkspace, path: "climate-actions" })}
            forceInactive={isClimateActionTeamContext}
          >
            <Leaf className="mr-2 h-3 w-3" />
            {t("sidebar.climateActions")}
          </SidebarNavElement>
          <ProjectWorkspaceSidebarNavLink workspace={currentWorkspace} forceInactive={isProjectTeamContext} />
          <IndicatorSidebarNavLink workspace={currentWorkspace} />

          <FeatureFlagToggle flag="enable-data-request">
            <SidebarNavElement to={makeWorkspaceUrl({ workspace: currentWorkspace, path: "data-requests" })}>
              <ClipboardList className="mr-2 h-3 w-3" />
              {t("sidebar.dataRequests")}
            </SidebarNavElement>
          </FeatureFlagToggle>
          <SidebarNavElement to={makeWorkspaceUrl({ workspace: currentWorkspace, path: "public-dashboards" })}>
            <LayoutDashboard className="mr-2 h-3 w-3" />
            {t("sidebar.dashboards")}
          </SidebarNavElement>
          {hasPermissionToManageWorkspace && (
            <SidebarNavElement to={makeWorkspaceUrl({ workspace: currentWorkspace, path: "activity-log" })}>
              <HistoryIcon className="mr-2 h-3 w-3" />
              {t("sidebar.activityLog")}
            </SidebarNavElement>
          )}
          <FeatureFlagToggle flag="enable-offering">
            <SidebarNavElement to={makeWorkspaceUrl({ workspace: currentWorkspace, path: "offerings" })}>
              <ArkLogoMark className="mr-2 h-4 w-4" />
              {t("sidebar.offering")}
            </SidebarNavElement>
          </FeatureFlagToggle>
        </>
      )}
    </>
  );
};
